import { toastLocalizedOperationCatcher, toastSuccessMessage } from "./alerts";
import { getLastCourseIdFromCookie } from "./auth";

import { showDeleteConfirmationModal } from "components/modals/DeleteSectionModal";
import { SectionRoleType, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentCourse } from "store/selectors";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { useNavigate } from "utils/routing";

export const COURSE_ROLES = ["student", "course_admin", "instructor"] as SectionRoleType[];

export const updateCourse = (course: Partial<SectionType> & { id: string }) => {
  return storeApi.courses.partial_update(course);
};

export const deleteCourse = (courseId: string) => {
  storeApi.courses.destroy(courseId);
};

export const useUserCourseSettings = () => {
  const { canAuthorCourse } = useCapabilities();
  const course = useAppSelector(selectCurrentCourse);
  const canSeeAIPanel = canAuthorCourse || course?.student_ai_assistant_enabled;
  const hideStaticContentForStudents = course?.hide_static_blocks_on_student_response_page;

  return {
    canSeeAIPanel,
    hideStaticContentForStudents,
  };
};

export const getDefaultCourse = (courses: SectionType[]): SectionType => {
  const lastAccessedCourseId = getLastCourseIdFromCookie();
  if (lastAccessedCourseId) {
    const course = courses.find((c) => c.id === lastAccessedCourseId);

    if (course) {
      return course;
    }
  }

  return courses[0];
};

export const useCourseSettingActions = () => {
  const navigate = useNavigate();
  const homeUrl = useHomeUrl();

  const handleCourseDelete = async (course: SectionType) => {
    const courseId = course.id;
    const courseTitle = course.title;

    const didDelete = await showDeleteConfirmationModal({ courseId, courseTitle });
    if (didDelete) {
      storeApi.courses
        .destroy(courseId)
        .then(() => {
          toastSuccessMessage(t("settings.delete_course_success"));
          navigate(homeUrl);
        })
        .catch(
          toastLocalizedOperationCatcher("delete_failure", {
            item: t("glossary.course"),
          }),
        );
    }
  };

  return {
    handleCourseDelete,
  };
};

export const getCourseResourceRole = (
  role: SectionRoleType,
): "admin" | "student" | "instructor" | "recording_agent" =>
  role === "course_admin" ? "admin" : role;
